import React, { Suspense } from 'react';
import { FlatFlow } from 'new/entities/FlatFlow';
import {
  RESOURCE_TYPE_DATA_SET,
  RESOURCE_TYPE_SINK,
  RESOURCE_TYPE_SOURCE,
} from 'js/constants/resourceTypes';
import { useHistory } from 'react-router-dom';
import { ResourceTriage } from 'new/components/monitor/ResourceTriage';
import { FitViewOptions } from '@xyflow/react';
import useEntities from 'new/state/entities/repository/useEntities';
import { useFlowMetricsSync } from 'new/state/metrics/useFlowMetricsSync';

/**
 * Lazy Imports
 */
const LeafPipelinePreviewContainer = React.lazy(
  () => import('js/components/pipelines/LeafPipelinePreviewContainer'),
);

export const PreviewFlowLeaf = (
  row: FlatFlow & {
    height: number;
    fitViewOptions?: FitViewOptions;
    isDataFlowStatus?: boolean;
  },
) => {
  const history = useHistory();
  const { data: flowNodes } = useEntities('flowNodes');

  // This leaf preview renders FlowNodes in isPreview mode, which disables its
  // own metrics fetch — so without this the interval toggle (shown on the canvas
  // in the Flows table) loaded no metrics and did nothing when changed. Drive
  // metrics from here: fetch on mount + on toggle.
  //
  // Exception: the 24h Data Flow Status view (isDataFlowStatus) renders its node
  // numbers from a dedicated dashboard API (state.metrics.dashboard), NOT from
  // this interval-driven fetch — so here the fetch would be a wasteful request
  // this view never reads (and the toggle is hidden, see FlowNodes). Disable it.
  const originNodeId = row.root?.origin_node_id ?? row.root?.flow_node_id ?? null;
  const sourceId = originNodeId != null ? flowNodes?.[originNodeId]?.data_source_id : null;
  useFlowMetricsSync(originNodeId, sourceId, { enabled: !row.isDataFlowStatus });

  /**
   * Redirect the user to the resource triage page with the selected resource
   */
  const onClickNode = (_: any, data: { id: any; type: any }) => {
    const { id, type } = data;
    let metricsResourceType: string;

    switch (type) {
      case 'SOURCE':
        metricsResourceType = RESOURCE_TYPE_SOURCE;
        break;
      case 'DATA_SINK':
        metricsResourceType = RESOURCE_TYPE_SINK;
        break;

      default:
        metricsResourceType = RESOURCE_TYPE_DATA_SET;
    }

    history.push(ResourceTriage.screenConfig.path + `/?type=${metricsResourceType}&id=${id}`);
  };

  return (
    <Suspense fallback={<div>loading...</div>}>
      <LeafPipelinePreviewContainer {...row} isNodeDiscoveryEnabled onItemClick={onClickNode} />
    </Suspense>
  );
};
